import React, { useCallback, useState } from 'react'

import { useExecutionForm, useExecutionFormActions } from './_form/ExecutionFormHandler'
import { useIsBulkExecution, useQuestions, useReplies, useServiceOrder } from './_load/ServiceOrderLoader'
import DynamicForm from '../../../containers/dynamic/form/DynamicForm'
import GeneralAccordion from './general/GeneralAccordion'
import { getAutofillConfig } from '../../../containers/dynamic/form/_form/formatRepliesFormData'
import { useImperativeComponents } from '../../../containers/dynamic/form/replies/context/ImperativeComponentsContext'
import AutofillFocusContext from '../../../containers/dynamic/form/replies/context/AutofillFocusContext'
import { fetchAutofillValue } from './_requests/fetchAutofillValue'

const ExecutionForm = () => {
  const serviceOrder = useServiceOrder()
  const isBulk = useIsBulkExecution()
  const questions = useQuestions()
  const replies = useReplies()
  const form = useExecutionForm()
  const { setForm } = useExecutionFormActions()
  const { components } = useImperativeComponents()
  const [autofillLoading, setAutofillLoading] = useState(false)

  const handleChange = v => {
    setForm({ ...form, ...v })
  }

  // Autofill busca valores do local/usuário de UMA atividade — em execução em
  // massa as atividades selecionadas podem ter locais/usuários diferentes,
  // então o recurso é desativado para não aplicar o valor de uma atividade
  // às demais por engano.
  const autofillContext = React.useMemo(
    () =>
      serviceOrder && !isBulk
        ? {
            serviceOrder,
            placeFieldValues: serviceOrder.place?.fieldValues,
            userFieldValues: serviceOrder.user?.fieldValues
          }
        : undefined,
    [serviceOrder, isBulk]
  )

  const setAutofillValue = useCallback((questionId, value) => {
    if (value === undefined || value === null) return
    const comp = components?.current?.[questionId]
    if (comp?.setValue) comp.setValue(value)
  }, [components])

  const onApplyAutofillFocus = useCallback(
    async (questionId, question) => {
      if (!serviceOrder || isBulk || !components?.current) return
      const config = getAutofillConfig(question)
      if (!config) return

      const comp = components.current[questionId]
      if (!comp?.setValue) return

      const { source, fieldId } = config

      setAutofillLoading(true)
      try {
        const value = await fetchAutofillValue(serviceOrder, source, fieldId)
        if (value !== undefined && value !== null) {
          comp.setValue(value)
        }
      } finally {
        setAutofillLoading(false)
      }
    },
    [serviceOrder, isBulk, components]
  )

  const autofillFocusValue = React.useMemo(
    () => (setAutofillValue ? { setAutofillValue, onApplyAutofillFocus } : null),
    [setAutofillValue, onApplyAutofillFocus]
  )

  return (
    <AutofillFocusContext.Provider value={autofillFocusValue}>
      <DynamicForm
        questions={questions}
        replies={replies}
        onChange={handleChange}
        autofillContext={autofillContext}
      >
        <GeneralAccordion />
      </DynamicForm>
    </AutofillFocusContext.Provider>
  )
}

export default ExecutionForm
